<div class="modal-overlay">
  <div class="modal-container">

    <!-- Header -->
    <div class="modal-header">
      <h2>Assign New Task</h2>
      <mat-icon class="close-icon" (click)="closeModal()">close</mat-icon>
    </div>

    <mat-dialog-content>
      <div class="modal-body">
        <form [formGroup]="taskForm" class="form-body">

          <!-- ── Task Name ──────────────────────────────────── -->
          <div class="field-group">
            <label class="field-label">Task Name <span class="required">*</span></label>
            <input type="text" class="form-input" formControlName="task" placeholder="Enter task name">
            <span class="field-error" *ngIf="formSubmitted && taskForm.get('task')?.hasError('required')">
              Task name is required
            </span>
          </div>

          <!-- ── Hospital ──────────────────────────────────── -->
          <div class="field-group">
            <div class="label-row">
              <label class="field-label">Hospital</label>
              <button type="button" class="select-all-btn" (click)="toggleSelectAllHospitals()">
                <mat-icon>{{ allHospitalsSelected ? 'check_box' : someHospitalsSelected ? 'indeterminate_check_box' : 'check_box_outline_blank' }}</mat-icon>
                {{ allHospitalsSelected ? 'Deselect All' : 'Select All' }}
              </button>
            </div>
            <div class="select-wrap">
              <mat-select class="form-select" formControlName="hospitalIds" multiple
                          [placeholder]="selectedHospitalIds.length === 0 ? 'Choose hospitals…' : hospitalDisplayValue()"
                          (opened)="hospitalSearch = ''; focusHospitalSearch()">
                <mat-option disabled class="hospital-search-opt">
                  <input #hospitalSearchInput
                         class="hospital-search-input"
                         placeholder="Search hospitals…"
                         [ngModel]="hospitalSearch" [ngModelOptions]="{standalone: true}"
                         (ngModelChange)="hospitalSearch = $event"
                         (click)="$event.stopPropagation()"
                         (keydown)="$event.stopPropagation()">
                </mat-option>
                <mat-option *ngFor="let h of hospitalList" [value]="h.practiceSKey"
                  [style.display]="isHospitalVisible(h) ? '' : 'none'">
                  {{ h.practiceName }}
                </mat-option>
                <mat-option disabled *ngIf="filteredHospitalList.length === 0" class="no-results-opt">
                  No hospitals found
                </mat-option>
              </mat-select>
            </div>
            <span class="field-hint" *ngIf="selectedHospitalIds.length > 0">
              <span class="badge-count">{{ selectedHospitalIds.length }}</span>
              {{ selectedHospitalIds.length === hospitalList.length ? 'all hospitals' : selectedHospitalIds.length === 1 ? 'hospital' : 'hospitals' }} selected
            </span>
          </div>

          <!-- ── Quarter + Goal ────────────────────────────── -->
          <ng-container *ngIf="!goalQuarterDisabled">
            <div [class]="goalList.length > 0 ? 'two-col' : 'field-group'">
              <div class="field-group">
                <label class="field-label">Quarter</label>
                <div class="select-wrap">
                  <select class="form-select" formControlName="quarter">
                    <option [ngValue]="null">Choose quarter…</option>
                    <option *ngFor="let q of quarterList" [ngValue]="q">{{ q.quarterName }}</option>
                  </select>
                  <span class="material-icons arrow">expand_more</span>
                </div>
              </div>
              <div class="field-group" *ngIf="goalList.length > 0">
                <label class="field-label">Goal</label>
                <div class="select-wrap">
                  <select class="form-select" formControlName="affiliatedGoal">
                    <option [ngValue]="0">None</option>
                    <option *ngFor="let goal of goalList" [ngValue]="goal.key">
                      {{ goal.goalName }}{{ isBulkMode ? ' (' + goal.covered + ' of ' + goal.total + ')' : '' }}
                    </option>
                  </select>
                  <span class="material-icons arrow">expand_more</span>
                </div>
              </div>
            </div>

            <!-- ── Partial goal coverage notice ─────────────── -->
            <div class="lock-notice" *ngIf="selectedGoalInfo?.isPartial">
              <mat-icon>info_outline</mat-icon>
              <span>
                <strong>{{ selectedGoalInfo.covered }} of {{ selectedGoalInfo.total }}</strong> selected hospitals
                have <strong>{{ selectedGoalInfo.goalName }}</strong> set up for this quarter. Hospitals without it
                won't receive a task: <strong>{{ skippedHospitalNames.join(', ') }}</strong>
              </span>
            </div>
          </ng-container>

          <!-- ── Lock notice ───────────────────────────────── -->
          <div class="lock-notice" *ngIf="goalQuarterDisabled">
            <mat-icon>lock_outline</mat-icon>
            <span *ngIf="isMultiHospital && isEditMode">
              Quarter &amp; Goal unavailable — editing only applies to one hospital at a time
            </span>
            <span *ngIf="!(isMultiHospital && isEditMode)">
              Quarter &amp; Goal unavailable for <strong>recurring</strong> tasks
            </span>
          </div>

          <!-- ── Assignee ──────────────────────────────────── -->
          <div class="field-group assignee-wrap">
            <div class="label-row">
              <label class="field-label">Assignee{{ isBulkMode ? 's' : '' }} <span class="required" *ngIf="assigneeRequired">*</span></label>
              <button type="button" class="assign-me-btn" *ngIf="currentUser && !isCurrentUserAssigned"
                      (click)="assignToMe()">
                Assign to Me
              </button>
            </div>
            <div class="assignee-chips" *ngIf="!isEditMode && selectedAssignees.length">
              <span class="assignee-chip" *ngFor="let a of selectedAssignees">
                {{ a.displayName }}
                <mat-icon (click)="removeAssignee(a)">close</mat-icon>
              </span>
            </div>
            <div class="assignee-input-row">
              <input type="text" class="form-input" placeholder="Search by name or email…"
                     [value]="assigneeQuery"
                     (input)="onAssigneeInput($event)"
                     (blur)="showAssigneeDrop = false"
                     autocomplete="off">
              <button type="button" class="clear-assignee" *ngIf="isEditMode && selectedAssignees.length" (click)="clearAssignee()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <span class="field-error" *ngIf="formSubmitted && taskForm.get('attendeesIds')?.hasError('required')">
              Assignee is required — type a name or check a role above
            </span>
            <div class="assignee-drop" *ngIf="showAssigneeDrop" (mousedown)="$event.preventDefault()">
              <div class="assignee-option" *ngFor="let u of assigneeSuggestions"
                   (mousedown)="selectAssignee(u)">
                <span class="ao-name">{{ u.displayName }}</span>
                <span class="ao-email">{{ u.email }}</span>
              </div>
            </div>

            <!-- ── Assign to all: RMs / DOs / VPs ───────────── -->
            <ng-container *ngIf="isBulkMode">
              <div class="assign-all-row">
                <span class="field-label" style="margin:0">Assign to all:</span>
                <button type="button" class="toggle-chip" *ngFor="let role of roleOptions"
                        [class.active]="isRoleChecked(role)" (click)="toggleRole(role)">
                  <mat-icon>{{ isRoleChecked(role) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
                  {{ role }}s
                </button>
              </div>
              <span class="chip-hint" *ngIf="isRoleChecked('RM') || isRoleChecked('DO') || isRoleChecked('VP')">
                Resolves to the person mapped to each hospital when you save.
              </span>

              <!-- ── Fan-out preview ───────────────────────── -->
              <div class="task-preview" *ngIf="selectedAssignees.length || (taskForm.get('assignToRoles')?.value || []).length">
                <div class="preview-head">
                  <strong>{{ totalTaskCount }} task{{ totalTaskCount === 1 ? '' : 's' }} will be created</strong>
                  <span class="preview-sub">· {{ activeHospitals.length }} hospital{{ activeHospitals.length === 1 ? '' : 's' }}</span>
                </div>
                <div class="preview-row" *ngFor="let p of assigneePreview">
                  <span class="ph-name">{{ p.hospital.practiceName }}</span>
                  <span class="ph-people" *ngIf="p.people.length; else noOne">
                    <ng-container *ngFor="let person of p.people; let last = last">
                      {{ person.displayName }}<span class="role-badge" *ngIf="person.role">{{ person.role }}</span>{{ !last ? ' · ' : '' }}
                    </ng-container>
                  </span>
                  <ng-template #noOne><span class="ph-warn">No one to assign here</span></ng-template>
                  <span class="ph-warn" *ngIf="p.missingRoles.length">Missing: {{ p.missingRoles.join(', ') }}</span>
                </div>
                <div class="preview-row skipped" *ngFor="let h of skippedHospitals">
                  <span class="ph-name">{{ h.practiceName }}</span>
                  <span class="ph-skip">Skipped — goal not set up here</span>
                </div>
              </div>
            </ng-container>
          </div>

          <!-- ── Category + Due Date ───────────────────────── -->
          <div class="two-col">
            <div class="field-group">
              <label class="field-label">Category <span class="required">*</span></label>
              <div class="select-wrap">
                <select class="form-select" formControlName="category">
                  <option [ngValue]="null">Choose a category</option>
                  <option *ngFor="let cat of categoryList" [ngValue]="cat.id">{{ cat.name }}</option>
                </select>
                <span class="material-icons arrow">expand_more</span>
              </div>
              <span class="field-error" *ngIf="formSubmitted && taskForm.get('category')?.hasError('required')">
                Category is required
              </span>
            </div>

            <div class="field-group" *ngIf="!isRecurring">
              <label class="field-label">Due Date <span class="required">*</span></label>
              <div class="date-wrap">
                <input class="form-input" [matDatepicker]="picker" formControlName="dueDate"
                       [min]="minDate" placeholder="MM/DD/YYYY" readonly>
                <mat-datepicker-toggle matIconSuffix [for]="picker" class="date-toggle"></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
              </div>
              <span class="field-error" *ngIf="formSubmitted && taskForm.get('dueDate')?.hasError('required')">
                Due date is required
              </span>
            </div>
          </div>

          <!-- ── Recurrence ─────────────────────────────────  -->
          <div class="field-group">
            <label class="field-label">Recurrence</label>
            <div class="recur-chips">
              <button type="button" class="recur-chip"
                      [class.active]="taskForm.get('recurringInterval')?.value === 'One-time'"
                      (click)="taskForm.get('recurringInterval')!.setValue('One-time')">
                One-time
              </button>
              <button type="button" class="recur-chip"
                      *ngFor="let opt of recurringOptions"
                      [class.active]="taskForm.get('recurringInterval')?.value === opt.value"
                      (click)="taskForm.get('recurringInterval')!.setValue(opt.value)">
                {{ opt.label }}
              </button>
            </div>
          </div>

          <!-- ── Recurring Details (End Date & Due Days) ── -->
          <div class="two-col" *ngIf="isRecurring">
            <div class="field-group">
              <label class="field-label">End Date <span class="required">*</span></label>
              <div class="date-wrap">
                <input class="form-input" [matDatepicker]="endPicker" formControlName="endDate"
                       [min]="minEndDate" placeholder="MM/DD/YYYY" readonly>
                <mat-datepicker-toggle matIconSuffix [for]="endPicker" class="date-toggle"></mat-datepicker-toggle>
                <mat-datepicker #endPicker></mat-datepicker>
              </div>
              <span class="field-error" *ngIf="formSubmitted && taskForm.get('endDate')?.hasError('required')">
                End date is required
              </span>
            </div>

            <div class="field-group">
              <label class="field-label">Due Days from Start Date</label>
              <input type="number" class="form-input" formControlName="dueDaysFromStartDate" min="0" placeholder="e.g. 10">
            </div>
          </div>

          <!-- ── Upcoming Occurrences ──────────────── -->
          <div class="field-group" *ngIf="isRecurring && upcomingOccurrences.length > 0">
            <div class="label-row">
              <label class="field-label">Upcoming Occurrences</label>
              <button type="button" class="view-schedule-btn" (click)="showOccurrences = !showOccurrences">
                <mat-icon>{{ showOccurrences ? 'visibility_off' : 'visibility' }}</mat-icon>
                {{ showOccurrences ? 'Hide' : 'View' }} Schedule
              </button>
            </div>
            <div class="occurrences-table-wrap" *ngIf="showOccurrences">
              <table class="occurrences-table">
                <thead>
                  <tr>
                    <th>Occurrence Date</th>
                    <th>Calculated Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let occ of upcomingOccurrences">
                    <td>{{ occ.occurrenceDate | date:'mediumDate' }}</td>
                    <td>{{ occ.calculatedDueDate | date:'mediumDate' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ── Description ────────────────────────────────  -->
          <div class="field-group">
            <label class="field-label">Description</label>
            <div class="description-editor-wrap">
              <ngx-editor-menu [editor]="descriptionEditor" [toolbar]="descriptionToolbar"
                              dropdownPlacement="top"></ngx-editor-menu>
              <ngx-editor formControlName="taskDescription"
                          [editor]="descriptionEditor"
                          [outputFormat]="'html'"
                          [placeholder]="'Optional — add context for the assignee'"></ngx-editor>
            </div>
          </div>

        </form>
      </div>
    </mat-dialog-content>

    <!-- Footer -->
    <mat-dialog-actions>
      <div class="modal-footer">
        <button class="cancel-btn" (click)="closeModal()">Cancel</button>
        <button type="button" class="v-button v-button-md v-green" [disabled]="loading" (click)="onSubmit()">
          <ng-container *ngIf="!loading">
            {{ isBulkMode ? 'Save ' + totalTaskCount + ' Task' + (totalTaskCount === 1 ? '' : 's') : 'Save Task' }}
          </ng-container>
          <ng-container *ngIf="loading">
            <i class="pi pi-spin pi-spinner"></i> Saving…
          </ng-container>
        </button>
      </div>
    </mat-dialog-actions>

  </div>
</div>
